import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../utils";
import { Separator } from "./Separator";
import { Badge } from "./Badge";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandSeparator } from "./Command";
import { SIcon } from "../../components";
const multiSelectVariants = cva("", {
    variants: {
        variant: {
            default: "bg-grayscale-light bg-opacity-50 hover:bg-opacity-90",
            secondary: "bg-grayscale-light bg-opacity-70 hover:bg-opacity-90",
            destructive: "bg-destructive text-destructive-foreground hover:bg-opacity-90",
            inverted: "inverted"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});
export const MultiSelect = /*#__PURE__*/ React.forwardRef(function MultiSelect({ options, onChange, value, variant, placeholder = "Select options", maxCount = 3, modalPopover = false, asChild = false, className, ...props }, ref) {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    // Update handlers to use onChange
    const toggleOption = (optionToToggle)=>{
        const newValue = value.some((v)=>v.value === optionToToggle.value) ? value.filter((v)=>v.value !== optionToToggle.value) : [
            ...value,
            optionToToggle
        ];
        onChange(newValue);
    };
    const handleClear = ()=>{
        onChange([]);
    };
    const toggleAll = ()=>{
        if (value.length === options.length) {
            handleClear();
        } else {
            onChange([
                ...options
            ]);
        }
    };
    return /*#__PURE__*/ _jsxs(Popover, {
        open: isPopoverOpen,
        onOpenChange: setIsPopoverOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsx("div", {
                    ref: ref,
                    ...props,
                    onClick: ()=>setIsPopoverOpen((prev)=>!prev),
                    className: cn("appearance-none !active:!scale-100 shadow-none rounded-md border border-white border-opacity-10", "focus:outline-0 focus:border-solid focus:border-opacity-25 hover:border-opacity-25", "bg-grayscale-lightest bg-opacity-10 py-2 pl-2 pr-3 h-10 w-full text-white cursor-pointer", "flex items-center justify-between w-full", className),
                    children: value.length > 0 ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between items-center w-full",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-1.5 flex-wrap items-center",
                                children: [
                                    value.slice(0, 3).map((value)=>{
                                        const option = options.find((o)=>o.value === value.value);
                                        return /*#__PURE__*/ _jsxs(Badge, {
                                            className: cn("text-white/[0.85] !pl-2.5 pr-1.5 gap-0.5 border-white/20 hover:border-white/[0.35] transition-colors hover:text-white group/badge h-7 !bg-grayscale-medium", multiSelectVariants({
                                                variant
                                            })),
                                            children: [
                                                option?.label,
                                                /*#__PURE__*/ _jsx("button", {
                                                    className: "ml-1.5 h-4 w-4 cursor-pointer",
                                                    onClick: (event)=>{
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                        toggleOption(value);
                                                    },
                                                    children: /*#__PURE__*/ _jsx(SIcon, {
                                                        type: "x",
                                                        className: "h-2.5 w-2.5 text-white/50 hover:text-white/80 transition-colors"
                                                    })
                                                })
                                            ]
                                        }, value.value);
                                    }),
                                    value.length > 3 && /*#__PURE__*/ _jsxs(Badge, {
                                        className: cn("text-white/[0.85] !pl-2.5 pr-1.5 gap-0.5 border-white/20 hover:border-white/[0.35] transition-colors hover:text-white group/badge h-7 !bg-grayscale-medium", multiSelectVariants({
                                            variant
                                        })),
                                        children: [
                                            `+ ${value.length - 3} more`,
                                            /*#__PURE__*/ _jsx("button", {
                                                className: "ml-2 h-4 w-4 cursor-pointer",
                                                onClick: (event)=>{
                                                    event.stopPropagation();
                                                    handleClear();
                                                },
                                                children: /*#__PURE__*/ _jsx(SIcon, {
                                                    type: "x",
                                                    className: "h-2.5 w-2.5 text-white/50 hover:text-white/80 transition-colors"
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2 items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        className: "h-4 cursor-pointer text-white/30",
                                        onClick: (event)=>{
                                            event.stopPropagation();
                                            handleClear();
                                        },
                                        children: /*#__PURE__*/ _jsx(SIcon, {
                                            type: "x",
                                            className: "h-[13px] w-[13px] mr-1 transition-colors hover:text-red-400/90"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Separator, {
                                        orientation: "vertical",
                                        className: "flex h-6"
                                    }),
                                    /*#__PURE__*/ _jsx(SIcon, {
                                        type: "chevron-down",
                                        className: "h-3.5 w-3.5 cursor-pointer transition-colors stroke-[1px] text-white/30"
                                    })
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm text-[#6B7280] mx-3",
                                children: placeholder
                            }),
                            /*#__PURE__*/ _jsx(SIcon, {
                                type: "chevron-down",
                                className: "h-3.5 w-3.5 cursor-pointer transition-colors stroke-[1px] text-white/30"
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(PopoverContent, {
                className: "w-auto p-0",
                align: "start",
                onEscapeKeyDown: ()=>setIsPopoverOpen(false),
                children: /*#__PURE__*/ _jsxs(Command, {
                    children: [
                        /*#__PURE__*/ _jsx(CommandInput, {
                            placeholder: "Search..."
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "max-h-[300px] overflow-y-auto overflow-x-hidden",
                            children: [
                                /*#__PURE__*/ _jsx(CommandEmpty, {
                                    children: "No results found."
                                }),
                                /*#__PURE__*/ _jsxs(CommandGroup, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(CommandItem, {
                                            onSelect: toggleAll,
                                            className: "cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: cn("mr-1.5 flex h-4 w-4 items-center justify-center rounded-sm border border-primary", value.length === options.length ? "bg-brand-primary text-[#13151A] border-transparent" : "opacity-50 [&_svg]:invisible"),
                                                    children: /*#__PURE__*/ _jsx(SIcon, {
                                                        type: "check",
                                                        className: cn("!h-2.5 !w-2.5", !(value.length === options.length) && "hidden")
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: "(Select All)"
                                                })
                                            ]
                                        }, "all"),
                                        options.map((option)=>{
                                            const isSelected = value.some((v)=>v.value === option.value);
                                            return /*#__PURE__*/ _jsxs(CommandItem, {
                                                onSelect: ()=>toggleOption(option),
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: cn("mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary", isSelected ? "bg-brand-primary text-[#13151A] border-transparent" : "opacity-50 [&_svg]:invisible"),
                                                        children: /*#__PURE__*/ _jsx(SIcon, {
                                                            type: "check",
                                                            className: cn("!h-2.5 !w-2.5", !isSelected && "hidden")
                                                        })
                                                    }),
                                                    option.label
                                                ]
                                            }, option.value);
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(CommandSeparator, {}),
                                /*#__PURE__*/ _jsx(CommandGroup, {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between",
                                        children: [
                                            value.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(CommandItem, {
                                                        onSelect: handleClear,
                                                        className: "flex-1 justify-center text-white/80 hover:text-white cursor-pointer",
                                                        children: "Clear"
                                                    }),
                                                    /*#__PURE__*/ _jsx(Separator, {
                                                        orientation: "vertical",
                                                        className: "flex h-6 mx-1"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(CommandItem, {
                                                onSelect: ()=>setIsPopoverOpen(false),
                                                className: "flex-1 justify-center text-white/80 hover:text-white cursor-pointer max-w-full",
                                                children: "Close"
                                            })
                                        ]
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
});
